import FormControl from '@material-ui/core/FormControl'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { INavParams } from 'pared/Routes/navParams'
import FeV2ChatBox from 'pared/Routes/renderer/feV2ChatBox'
import useGroupSelector from 'pared/components/GroupSelector/hooks/useGroupSelector'
import SousChefChatBox from 'pared/components/SousChefChatBox'
import ExpoSelect from 'pared/components/basicUi/select'
import DateRangeSelector from 'pared/components/nav/DateRangeSelector'
import { BRAND_LOCATION_GROUP_ID } from 'pared/constants/brands'
import { EDDIE_MERLOTS_BRAND_CODE } from 'pared/constants/brands'
import { MOBILE_WIDTH } from 'pared/constants/styles'
import useGetDateRange from 'pared/hooks/useGetDateRange'
import useGetDirectors from 'pared/hooks/useGetDirectors'
import DirectorFilterSelector from 'pared/pages/StoreList/DirectorFilterSelector'
import { getBrand } from 'pared/utils/brand'

import HistoricalPerformance from './HistoricalPerformance'
import PerformanceMartix from './PerformanceMartix'
import useFeV2ChatConfig from './hooks/useFeV2ChatConfig'
import useSousChefConfig from './hooks/useSousChefConfig'

interface IPropsType {
  navParams: INavParams
}

const Header = styled.div`
  display: flex;
  width: 1200px;
  justify-content: space-between;

  @media ${MOBILE_WIDTH} {
    margin: 0px;
    flex-direction: column-reverse;
    width: 100%;
  }

  h1 {
    margin: 0px;
    font-family: Lexend-SemiBold;
    font-size: 36px;
    font-weight: 700;
    line-height: 52px;

    @media ${MOBILE_WIDTH} {
      padding: 30px 25px 0px;
    }
  }
`

const SubtitleContainer = styled.div`
  font-family: Lexend-Regular;
  font-style: italic;

  @media ${MOBILE_WIDTH} {
    position: initial;
    height: auto;
    padding: 0px 25px;
  }
`

const Selectors = styled.div`
  @media ${MOBILE_WIDTH} {
    padding: 10px 25px;
    background-color: rgb(196, 196, 196);
  }

  & > * {
    &:not(:last-child) {
      margin-right: 7px;

      @media ${MOBILE_WIDTH} {
        margin-right: 0px;
      }
    }

    @media ${MOBILE_WIDTH} {
      width: 100% !important;
    }
  }
`

const Container = styled.div`
  @media ${MOBILE_WIDTH} {
    padding: 30px 25px 50px;
  }
`

const StyledFormControl = styled(FormControl)`
  background-color: white;
  @media ${MOBILE_WIDTH} {
    width: 100%;
  }
`

const Product = ({ navParams }: IPropsType) => {
  const sousChefConfig = useSousChefConfig()
  const feV2ChatConfig = useFeV2ChatConfig()
  const brand = getBrand()
  const shouldUseGroupSelector = ['mwb'].includes(brand)
  const dateRange = useGetDateRange()
  const { director, directors, directorChange, locationGroupIds } =
    useGetDirectors()
  const { groupBy, setGroupBy, options, locationGroupId } = useGroupSelector({
    initialGroupBy: 'store',
    shouldHideBreakdown: true,
  })
  const currentLocationGroupIds = useMemo(() => {
    if (shouldUseGroupSelector)
      return [parseInt(locationGroupId) || BRAND_LOCATION_GROUP_ID[brand]]

    return locationGroupIds
  }, [shouldUseGroupSelector, brand, locationGroupId, locationGroupIds])

  const subtitle = (() => {
    switch (brand) {
      case 'mwb':
        return null
      case EDDIE_MERLOTS_BRAND_CODE:
        return (
          <>
            <SubtitleContainer>Main Dining and Bar Only</SubtitleContainer>
            <SubtitleContainer>
              This page uses CrunchTime data, so some locations may not be
              included yet
            </SubtitleContainer>
          </>
        )
      default:
        return <SubtitleContainer>Main Dining and Bar Only</SubtitleContainer>
    }
  })()

  return (
    <>
      <Header>
        <div>
          <h1>Product</h1>

          {subtitle}
        </div>

        <Selectors>
          {shouldUseGroupSelector ? (
            <StyledFormControl margin="dense">
              <ExpoSelect
                value={groupBy}
                onChange={setGroupBy}
                dataSource={options}
                fullWidth
                displayEmpty
              />
            </StyledFormControl>
          ) : (
            <DirectorFilterSelector
              selectedDirector={director}
              allDirectors={directors}
              onDirectorChange={directorChange}
              disabledDirectors
            />
          )}

          <DateRangeSelector navParams={navParams} />
        </Selectors>
      </Header>

      <Container>
        <PerformanceMartix
          dateRange={dateRange}
          locationGroupIds={currentLocationGroupIds}
        />

        <HistoricalPerformance
          dateRange={dateRange}
          locationGroupIds={currentLocationGroupIds}
        />

        {sousChefConfig.enableChat && (
          <SousChefChatBox
            agentCode={sousChefConfig.agentCode}
            sendStoreId={false}
            hideFromNonExpoUsers={sousChefConfig.hideFromNonExpoUsers}
          />
        )}

        {feV2ChatConfig.enableChat && (
          <FeV2ChatBox
            type="feV2-chatbox"
            agentType={feV2ChatConfig.agentType}
            enabled={feV2ChatConfig.enableChat}
            description={feV2ChatConfig.description}
            sendStoreId={feV2ChatConfig.sendStoreId}
            hideFromNonExpoUsers={feV2ChatConfig.hideFromNonExpoUsers}
            disableInputField={feV2ChatConfig.disableInputField}
            bubblePrompts={feV2ChatConfig.bubblePrompts}
          />
        )}
      </Container>
    </>
  )
}

export default React.memo(Product) as unknown as typeof Product
